import {
  GAMEPLAY_CUT_CLASSES,
  GameplayCutClass,
  GameplayCutConfig,
  GameplayCutOutputMode,
  GameplayCutProfile,
  ResolvedGameplayCut,
} from "./store/types"

export const ALWAYS_CUT_CLASSES: GameplayCutClass[] = ["pre_post_match", "outside_game"]

export const NO_DETECTOR_CLASSES: GameplayCutClass[] = ["in_match_ui", "no_control", "match_transition"]

export const GAMEPLAY_CUT_CLASS_LABELS: Record<GameplayCutClass, string> = {
  live_play: "Live play",
  in_match_ui: "In-match UI",
  no_control: "No control",
  match_transition: "Match transition",
  pre_post_match: "Pre/post match",
  outside_game: "Outside game",
}

export const GAMEPLAY_CUT_PROFILE_OPTIONS: Array<{ value: GameplayCutProfile; label: string }> = [
  { value: "soft", label: "soft — keep live play, in-match UI, no control, transitions" },
  { value: "soft_plus", label: "soft_plus — soft, but cut no control" },
  { value: "harsh_lenient", label: "harsh_lenient — live play only, 30s short break" },
  { value: "harsh_strict", label: "harsh_strict — live play only, no short break" },
]

export const GAMEPLAY_CUT_OUTPUT_OPTIONS: Array<{ value: GameplayCutOutputMode; label: string }> = [
  { value: "manifest", label: "manifest — whole files + kept intervals" },
  { value: "trimmed", label: "trimmed — cut the video files" },
]

export const DEFAULT_GAMEPLAY_CUT_PROFILE: GameplayCutProfile = "soft"

function keepOnly(...kept: GameplayCutClass[]): Record<GameplayCutClass, boolean> {
  return Object.fromEntries(GAMEPLAY_CUT_CLASSES.map(c => [c, kept.includes(c)])) as Record<GameplayCutClass, boolean>
}

const SHARED_DEFAULTS = {
  idleThresholdSec: 60,
  minBlockSec: 60,
  coplayMinSec: 60,
  coplayMinPlayers: 2,
  outputMode: "manifest" as GameplayCutOutputMode,
}

export const GAMEPLAY_CUT_PRESETS: Record<GameplayCutProfile, ResolvedGameplayCut> = {
  soft: {
    ...SHARED_DEFAULTS,
    profile: "soft",
    keep: keepOnly("live_play", "in_match_ui", "no_control", "match_transition"),
    shortBreakSec: null,
  },
  soft_plus: {
    ...SHARED_DEFAULTS,
    profile: "soft_plus",
    keep: keepOnly("live_play", "in_match_ui", "match_transition"),
    shortBreakSec: null,
  },
  harsh_lenient: {
    ...SHARED_DEFAULTS,
    profile: "harsh_lenient",
    keep: keepOnly("live_play"),
    shortBreakSec: 30,
  },
  harsh_strict: {
    ...SHARED_DEFAULTS,
    profile: "harsh_strict",
    keep: keepOnly("live_play"),
    shortBreakSec: 0,
  },
}

export function resolveGameplayCut(config: GameplayCutConfig | null | undefined): ResolvedGameplayCut {
  const preset = GAMEPLAY_CUT_PRESETS[config?.profile ?? DEFAULT_GAMEPLAY_CUT_PROFILE]
  const keep = { ...preset.keep, ...(config?.keep ?? {}) }
  for (const c of ALWAYS_CUT_CLASSES) {
    keep[c] = false
  }

  return {
    profile: preset.profile,
    keep,
    idleThresholdSec: config?.idleThresholdSec ?? preset.idleThresholdSec,
    shortBreakSec: config?.shortBreakSec !== undefined ? config.shortBreakSec : preset.shortBreakSec,
    minBlockSec: config?.minBlockSec ?? preset.minBlockSec,
    coplayMinSec: config?.coplayMinSec ?? preset.coplayMinSec,
    coplayMinPlayers: config?.coplayMinPlayers ?? preset.coplayMinPlayers,
    outputMode: config?.outputMode ?? preset.outputMode,
  }
}

export interface GameplayCutFormValues {
  profile: GameplayCutProfile
  keep: Record<GameplayCutClass, boolean>
  idleThresholdSec?: number | null
  shortBreakSec?: number | null
  minBlockSec?: number | null
  coplayMinSec?: number | null
  coplayMinPlayers?: number | null
  outputMode: GameplayCutOutputMode
}

const NUMERIC_KEYS = ["idleThresholdSec", "minBlockSec", "coplayMinSec", "coplayMinPlayers"] as const

export function formToGameplayCut(values: GameplayCutFormValues): GameplayCutConfig {
  const preset = GAMEPLAY_CUT_PRESETS[values.profile ?? DEFAULT_GAMEPLAY_CUT_PROFILE]
  const config: GameplayCutConfig = { profile: preset.profile }

  const keep: GameplayCutConfig["keep"] = {}
  for (const c of GAMEPLAY_CUT_CLASSES) {
    if (ALWAYS_CUT_CLASSES.includes(c)) continue
    const value = values.keep?.[c] ?? preset.keep[c]
    if (value !== preset.keep[c]) keep[c] = value
  }
  if (Object.keys(keep).length) config.keep = keep

  for (const key of NUMERIC_KEYS) {
    const value = values[key]
    if (value != null && value !== preset[key]) config[key] = value
  }

  const shortBreakSec = values.shortBreakSec ?? null
  if (shortBreakSec !== preset.shortBreakSec) config.shortBreakSec = shortBreakSec

  if (values.outputMode && values.outputMode !== preset.outputMode) config.outputMode = values.outputMode

  return config
}

export function cutClassesWithoutDetector(keep: Record<GameplayCutClass, boolean>): GameplayCutClass[] {
  return NO_DETECTOR_CLASSES.filter(c => !keep[c])
}

export const MAX_CUT_SECONDS = 86400

export const MAX_PREVIEW_RECORDINGS = 50

export const MAX_CUT_FLAG_LOOKUP_IDS = 500

export const DEFAULT_VOID_REASON = "disallowed_mode"
